import React, { useContext, useState, useEffect, useRef } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { Button, DialogOverlay, DialogOverlayFooter, DialogOverlayActions, TextField, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import { PersonContext } from '../PersonContext'
import { getMergeHistory, getPersonChanges, undoMerge } from '../../../services/personService'
import useNotifications from '../../../hooks/useNotifications'
import MergeRow from './MergeRow'
import RestoreRow from './RestoreRow'

export default function MergeHistory() {
  usePageView({ page_detail: 'DMConsole:MergeHistory' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.mergeHistory'))
  const { error } = useNotifications()
  const { currentPerson } = useContext(PersonContext)

  const [mergeHistory, setMergeHistory] = useState()
  const [reason, setReason] = useState('')
  const [changeId, setChangeId] = useState()
  const [personChanges, setPersonChanges] = useState()
  const dialogOpener = useOverlay()
  const focusElementRef = useRef()

  useEffect(() => {
    if (currentPerson.id) {
      getPersonChanges(currentPerson.id)
        .then((response) => {
          setPersonChanges(response.data?.changes)
        })
        .catch((err) => {
          error(
            `${t('person.merges.errors.gettingPersonChanges', 'Error getting person changes')}: ${err.message}`,
            err
          )
        })
    }
  }, [currentPerson.id, error, t])

  useEffect(() => {
    let isCurrent = true
    getMergeHistory(currentPerson.id, true)
      .then((response) => {
        if (isCurrent) {
          setMergeHistory(response.data)
        }
      })
      .catch((err) => {
        if (isCurrent) {
          error(
            t('person.merges.errors.gettingMergeHistory', {
              defaultValue: `Error getting merge history: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        }
      })
    return () => {
      isCurrent = false
    }
  }, [currentPerson, error, t])

  const isUndoDisabled = (merge) => {
    return !(personChanges?.[0]?.metadata?.changeId.slice(0, -4) === merge.changeId.slice(0, -4))
  }

  const handleOpen = (id) => {
    setChangeId(id)
    dialogOpener.open()
  }

  const handleClose = () => {
    dialogOpener.close()
    setChangeId()
  }

  const handleUndoMerge = () => {
    undoMerge(currentPerson.id, changeId, reason)
      .then((undoResponse) => {
        if (undoResponse.status === 200) {
          getMergeHistory(currentPerson.id)
            .then((historyResponse) => {
              handleClose()
              setMergeHistory(historyResponse.data)
            })
            .catch((err) => {
              error(
                t('person.merges.errors.successUndoingMergeErrorGettingMergeHistory', {
                  defaultValue: `Success undoing a merge, but error getting merge history: ${err.message || ''}`,
                  message: err.message || '',
                }),
                err
              )
            })
        }
      })
      .catch((failureResponse) => {
        handleClose()
        error(
          t('person.merges.errors.undoingMerge', {
            defaultValue: `Error undoing a merge: ${failureResponse.message || ''}`,
            message: failureResponse.message || '',
          }),
          failureResponse
        )
      })
  }

  if (!mergeHistory) {
    return null
  }
  if (mergeHistory.length === 0) {
    return <>{t('person.merges.noMergesFound', 'No merges found')}</>
  }

  return (
    <>
      <DmcResponsiveTable
        id="mergesTable"
        atSizeColumns={{
          default: {
            labels: [
              t('status.label', 'Status'),
              t('reason', 'Reason'),
              t('date', 'Date'),
              t('person.merges.survivingPerson', 'Surviving Person'),
              t('person.merges.duplicatePerson', 'Duplicate Person'),
              t('submitter', 'Submitter'),
              '',
            ],
          },
          lg: {
            labels: [
              t('status.label', 'Status'),
              t('reason', 'Reason'),
              t('date', 'Date'),
              `${t('person.merges.survivingPerson', 'Surviving Person')}/${t(
                'person.merges.duplicatePerson',
                'Duplicate Person'
              )}`,
              t('submitter', 'Submitter'),
            ],
            columnWidths: [1, 3, 2, 3, 3],
            combinationRules: [['column1'], ['column2'], ['column3'], ['column4', 'column5'], ['column6', 'column7']],
          },
          xl: {
            labels: [
              t('status.label', 'Status'),
              t('reason', 'Reason'),
              t('date', 'Date'),
              t('person.merges.survivingPerson', 'Surviving Person'),
              t('person.merges.duplicatePerson', 'Duplicate Person'),
              t('submitter', 'Submitter'),
              '',
            ],
            columnWidths: [1, 3, 1, 2, 2, 1, 2],
          },
        }}
        separator={<br />}
        totalColumnCount={7}
      >
        {mergeHistory &&
          mergeHistory.map((change) => {
            return (
              <React.Fragment key={change.changeId}>
                {change.operation === 'Restored' ? (
                  <RestoreRow change={change} />
                ) : (
                  <MergeRow merge={change} isUndoDisabled={isUndoDisabled} handleOpen={handleOpen} />
                )}
              </React.Fragment>
            )
          })}
      </DmcResponsiveTable>
      <DialogOverlay {...dialogOpener} focusRef={focusElementRef} title={t('person.merges.undoMerge', 'Undo Merge')}>
        {t('person.merges.undoMergeConfirmation', 'Are you sure you want to undo this merge?')}
        <TextField
          ref={focusElementRef}
          label={t('reason', 'Reason')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button id="confirmUndoMergeBtn" onClick={handleUndoMerge} emphasis="high">
              {t('action.confirm', 'Confirm')}
            </Button>
            <Button onClick={dialogOpener.close}>{t('action.cancel', 'Cancel')}</Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}
